import {ActionIcon, Button, Checkbox, Group, TextInput} from '@mantine/core'
import {useDebouncedValue} from '@mantine/hooks'
import {IconSearch, IconUpload, IconX} from '@tabler/icons-react'
import {useNavigate} from '@tanstack/react-router'
import {DataTable, type DataTableColumn, type DataTableSortStatus} from 'mantine-datatable'
import {useMemo, useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'

import {useWorkerTypes} from '../worker-types/use-worker-types'
import {employmentClassificationLabel, type Worker} from './schemas'
import {useCreateWorker, useWorkers} from './use-workers'
import {WorkerFormModal} from './worker-form-modal'
import {WorkerImportModal} from './worker-import-modal'

function hasActiveFilters(name: string) {
    return Boolean(name.trim())
}

function filterWorkers(workers: Worker[], nameFilter: string) {
    const name = nameFilter.trim().toLowerCase()
    if (!name) {
        return workers
    }
    return workers.filter((worker) => worker.name.toLowerCase().includes(name))
}

function compareWorkers(
    left: Worker,
    right: Worker,
    columnAccessor: DataTableSortStatus<Worker>['columnAccessor'],
    workerTypeNameById: Map<string, string>,
) {
    if (columnAccessor === 'workerTypeId') {
        const leftValue = workerTypeNameById.get(left.workerTypeId) ?? ''
        const rightValue = workerTypeNameById.get(right.workerTypeId) ?? ''
        return leftValue.localeCompare(rightValue)
    }

    if (columnAccessor === 'employmentClassification') {
        return employmentClassificationLabel(left.employmentClassification).localeCompare(
            employmentClassificationLabel(right.employmentClassification),
        )
    }

    const leftValue = String(left[columnAccessor as keyof Worker] ?? '')
    const rightValue = String(right[columnAccessor as keyof Worker] ?? '')
    return leftValue.localeCompare(rightValue)
}

function sortWorkers(
    workers: Worker[],
    sortStatus: DataTableSortStatus<Worker>,
    workerTypeNameById: Map<string, string>,
) {
    const directionMultiplier = sortStatus.direction === 'asc' ? 1 : -1
    return [...workers].sort(
        (left, right) =>
            directionMultiplier *
            compareWorkers(left, right, sortStatus.columnAccessor, workerTypeNameById),
    )
}

function columnFilterClearButton(value: string, onClear: () => void) {
    if (!value) {
        return null
    }

    return (
        <ActionIcon size="sm" variant="transparent" c="dimmed" onClick={onClear}>
            <IconX size={14} />
        </ActionIcon>
    )
}

export function WorkersPage() {
    const navigate = useNavigate()
    const user = useCurrentUser()
    const [nameFilter, setNameFilter] = useState('')
    const [debouncedName] = useDebouncedValue(nameFilter, 300)
    const [includeArchived, setIncludeArchived] = useState(false)
    const {data: workers = [], isLoading} = useWorkers({includeArchived})
    const {data: workerTypes = []} = useWorkerTypes()
    const createWorker = useCreateWorker()
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Worker>>({
        columnAccessor: 'name',
        direction: 'asc',
    })
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [importModalOpen, setImportModalOpen] = useState(false)

    const workerTypeNameById = useMemo(
        () => new Map(workerTypes.map((type) => [type.id, type.name])),
        [workerTypes],
    )

    const filtersActive = hasActiveFilters(debouncedName)

    const sortedWorkers = useMemo(
        () => sortWorkers(filterWorkers(workers, debouncedName), sortStatus, workerTypeNameById),
        [workers, debouncedName, sortStatus, workerTypeNameById],
    )

    const columns = useMemo((): DataTableColumn<Worker>[] => {
        return [
            {
                accessor: 'name',
                title: 'Name',
                sortable: true,
                filter: (
                    <TextInput
                        label="Worker name"
                        description="Show workers whose names include the specified text"
                        placeholder="Search worker names..."
                        leftSection={<IconSearch size={16} stroke={1.5} />}
                        rightSection={columnFilterClearButton(nameFilter, () => setNameFilter(''))}
                        value={nameFilter}
                        onChange={(event) => setNameFilter(event.currentTarget.value)}
                    />
                ),
                filtering: nameFilter !== '',
            },
            {
                accessor: 'workerTypeId',
                title: 'Type',
                sortable: true,
                render: (row) => workerTypeNameById.get(row.workerTypeId) ?? 'Unknown',
            },
            {
                accessor: 'employmentClassification',
                title: 'Employment classification',
                sortable: true,
                render: (row) => employmentClassificationLabel(row.employmentClassification),
            },
        ]
    }, [nameFilter, workerTypeNameById])

    return (
        <>
            <Group justify="space-between" mb="md">
                <Checkbox
                    label="Include archived"
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.currentTarget.checked)}
                />
                <Group>
                    {isAdmin(user) && (
                        <Button
                            variant="default"
                            leftSection={<IconUpload size={16} />}
                            onClick={() => setImportModalOpen(true)}
                        >
                            Import CSV
                        </Button>
                    )}
                    {isAdmin(user) && (
                        <Button onClick={() => setCreateModalOpen(true)}>Add worker</Button>
                    )}
                </Group>
            </Group>

            <DataTable
                withTableBorder
                withColumnBorders
                striped
                highlightOnHover
                textSelectionDisabled
                idAccessor="id"
                records={sortedWorkers}
                columns={columns}
                fetching={isLoading}
                minHeight={sortedWorkers.length === 0 ? 180 : undefined}
                noRecordsText={filtersActive ? 'No workers match your filters.' : 'No workers yet.'}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                onRowClick={({record}) =>
                    void navigate({
                        to: '/workers/$workerId',
                        params: {workerId: record.id},
                    })
                }
            />

            <WorkerFormModal
                opened={createModalOpen}
                workerTypes={workerTypes}
                onClose={() => setCreateModalOpen(false)}
                onCreate={(input) => createWorker.mutateAsync(input)}
                onSaved={() => setCreateModalOpen(false)}
            />

            <WorkerImportModal opened={importModalOpen} onClose={() => setImportModalOpen(false)} />
        </>
    )
}
